import api from './api';

export interface AdminUser {
  id: number;
  email: string | null;
  username: string | null;
  phone: string | null;
  name: string;
  role: 'admin' | 'user';
  partner_id: number | null;
  is_active: boolean;
  must_change_password: boolean;
  created_at: string;
  updated_at?: string;
  last_login?: string;
}

export interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  adminUsers: number;
  recentLogins: number;
  usersWithUsername?: number;
}

export interface OdooClient {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  vat: string | null;
  city: string | null;
  hasUser: boolean;
}

export interface ClientWithUser extends OdooClient {
  user?: AdminUser;
}

export interface SyncResult {
  total: number;
  new: number;
  existing: number;
  usersCreated: number;
  errors: Array<{ partnerId: number; name: string; error: string }>;
}

export interface CreateUserData {
  email?: string;
  phone?: string;
  name: string;
  role?: 'admin' | 'user';
  partner_id?: number;
  password?: string;
  generateUsernameFromName?: boolean;
}

export interface UpdateUserData {
  email?: string;
  phone?: string;
  username?: string;
  name?: string;
  role?: 'admin' | 'user';
  partner_id?: number | null;
  is_active?: boolean;
}

class AdminService {
  // Estadísticas
  async getStats(): Promise<AdminStats> {
    const response = await api.get('/admin/stats');
    return response.data.data;
  }

  // Usuarios
  async getUsers(params?: {
    search?: string;
    role?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<{ users: AdminUser[]; pagination: any }> {
    const response = await api.get('/admin/users', { params });
    return response.data.data;
  }

  async getUserById(id: number): Promise<AdminUser> {
    const response = await api.get(`/admin/users/${id}`);
    return response.data.data;
  }

  async createUser(data: CreateUserData): Promise<{ user: AdminUser; tempPassword?: string }> {
    const response = await api.post('/admin/users', data);
    return response.data.data;
  }

  async updateUser(id: number, data: UpdateUserData): Promise<AdminUser> {
    const response = await api.put(`/admin/users/${id}`, data);
    return response.data.data;
  }

  async deleteUser(id: number): Promise<void> {
    await api.delete(`/admin/users/${id}`);
  }

  async changeUserPassword(id: number, newPassword: string, mustChangeOnLogin: boolean = true): Promise<void> {
    await api.post(`/admin/users/${id}/change-password`, {
      newPassword,
      mustChangeOnLogin
    });
  }

  // Username
  async generateUsername(id: number): Promise<{ username: string }> {
    const response = await api.post(`/admin/users/${id}/generate-username`);
    return response.data.data;
  }

  async previewUsername(name: string): Promise<{ suggestedUsername: string; baseUsername: string }> {
    const response = await api.post('/admin/preview-username', { name });
    return response.data.data;
  }

  // Clientes con usuarios (nuevo endpoint optimizado)
  async getClientsWithUsers(params?: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ clients: ClientWithUser[]; total: number }> {
    const response = await api.get('/admin/clients', { params });
    return response.data.data;
  }

  // Odoo (legacy)
  async getOdooClients(params?: {
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<OdooClient[]> {
    const response = await api.get('/admin/odoo/clients', { params });
    return response.data.data;
  }

  async syncOdooClients(options?: {
    createUsers?: boolean;
    defaultPassword?: string;
    deltaOnly?: boolean;
  }): Promise<{ inProgress: boolean; progress: number; total: number }> {
    const response = await api.post('/admin/odoo/sync', options || {});
    return response.data.data;
  }

  async getSyncStatus(): Promise<{
    isRunning: boolean;
    progress: number;
    total: number;
    results: SyncResult | null;
    startedAt: string | null;
    completedAt: string | null;
    error: string | null;
    lastSyncDate: string | null;
  }> {
    const response = await api.get('/admin/odoo/sync-status');
    return response.data.data;
  }

  // Generar usernames masivamente
  async generateMissingUsernames(): Promise<{ updated: number; remaining?: number }> {
    const response = await api.post('/admin/generate-usernames');
    return response.data.data;
  }
}

const adminService = new AdminService();
export default adminService;
